"use client"

import React, {
  createContext,
  useContext,
  useId,
  forwardRef,
  useMemo,
} from "react"
import * as RechartsPrimitive from "recharts"
import { cn } from "../../lib/utils"

// Temas para variables CSS en tiempo de ejecución
const THEMES = { light: "", dark: ".dark" }

const ChartContext = createContext(null)

function useChart() {
  const context = useContext(ChartContext)
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />")
  }
  return context
}

/* -------------------------------------------------------------------------- */
/*  ChartContainer                                                            */
/* -------------------------------------------------------------------------- */
const ChartContainer = forwardRef(
  ({ id, className, children, config }, ref) => {
    const uniqueId = useId()
    const chartId = `chart-${(id || uniqueId).replace(/:/g, "")}`

    return (
      <ChartContext.Provider value={{ config }}>
        <div
          data-chart={chartId}
          ref={ref}
          className={cn(
            "flex aspect-video justify-center text-xs " +
              "[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground " +
              "[&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 " +
              "[&_.recharts-curve.recharts-tooltip-cursor]:stroke-border " +
              "[&_.recharts-dot[stroke='#fff']]:stroke-transparent " +
              "[&_.recharts-layer]:outline-none " +
              "[&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border " +
              "[&_.recharts-radial-bar-background-sector]:fill-muted " +
              "[&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted " +
              "[&_.recharts-reference-line_[stroke='#ccc']]:stroke-border " +
              "[&_.recharts-sector[stroke='#fff']]:stroke-transparent " +
              "[&_.recharts-sector]:outline-none " +
              "[&_.recharts-surface]:outline-none",
            className
          )}
        >
          <ChartStyle id={chartId} config={config} />
          <RechartsPrimitive.ResponsiveContainer>
            {children}
          </RechartsPrimitive.ResponsiveContainer>
        </div>
      </ChartContext.Provider>
    )
  }
)
ChartContainer.displayName = "ChartContainer"

/* -------------------------------------------------------------------------- */
/*  Dinámica de colores / temas                                               */
/* -------------------------------------------------------------------------- */
function ChartStyle({ id, config }) {
  const entries = Object.entries(config).filter(
    ([, cfg]) => cfg.theme || cfg.color
  )
  if (!entries.length) return null

  const css = Object.entries(THEMES)
    .map(([theme, prefix]) => {
      const lines = entries
        .map(([key, cfg]) => {
          const value = cfg.theme?.[theme] ?? cfg.color
          return value ? `  --color-${key}: ${value};` : null
        })
        .filter(Boolean)
        .join("\n")
      return `${prefix} [data-chart=${id}] {\n${lines}\n}`
    })
    .join("\n")

  // eslint-disable-next-line react/no-danger
  return <style dangerouslySetInnerHTML={{ __html: css }} />
}

/* -------------------------------------------------------------------------- */
/*  Tooltip                                                                   */
/* -------------------------------------------------------------------------- */
const ChartTooltip = RechartsPrimitive.Tooltip

const ChartTooltipContent = forwardRef(
  (
    {
      active,
      payload,
      className,
      indicator = "dot",
      hideLabel = false,
      hideIndicator = false,
      label,
      labelFormatter,
      labelClassName,
      formatter,
      color,
      nameKey,
      labelKey,
      /* props internos de Recharts los ignoramos aquí */
    },
    ref
  ) => {
    const { config } = useChart()

    const tooltipLabel = useMemo(() => {
      if (hideLabel || !active || !payload?.length) return null
      const item = payload[0]
      const key = labelKey || item.dataKey || item.name || "value"
      const cfg = getPayloadConfigFromPayload(config, item, key)
      let value =
        !labelKey && typeof label === "string"
          ? config[label]?.label || label
          : cfg?.label

      return labelFormatter && value != null
        ? labelFormatter(value, payload)
        : value
    }, [label, labelFormatter, payload, hideLabel, labelKey, config, active])

    if (!active || !payload?.length) return null

    const nestLabel = payload.length === 1 && indicator !== "dot"

    return (
      <div
        ref={ref}
        className={cn(
          "grid min-w-[8rem] items-start gap-1.5 rounded-lg border " +
            "border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl",
          className
        )}
      >
        {!nestLabel && (
          <div className={cn("font-medium", labelClassName)}>
            {tooltipLabel}
          </div>
        )}
        <div className="grid gap-1.5">
          {payload.map((item, i) => {
            const key = nameKey || item.name || item.dataKey || "value"
            const cfg = getPayloadConfigFromPayload(config, item, key)
            const dotColor = color || item.payload.fill || item.color

            return (
              <div
                key={i}
                className={cn(
                  "flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 " +
                    "[&>svg]:w-2.5 [&>svg]:text-muted-foreground",
                  indicator === "dot" && "items-center"
                )}
              >
                {!formatter ? (
                  <>
                    {cfg?.icon ? (
                      <cfg.icon />
                    ) : (
                      !hideIndicator && (
                        <div
                          className={cn(
                            "shrink-0 rounded-[2px] border-[--color-border] " +
                              "bg-[--color-bg]",
                            {
                              "h-2.5 w-2.5": indicator === "dot",
                              "w-1": indicator === "line",
                              "w-0 border-[1.5px] border-dashed bg-transparent":
                                indicator === "dashed",
                              "my-0.5": nestLabel && indicator === "dashed",
                            }
                          )}
                          style={{
                            "--color-bg": dotColor,
                            "--color-border": dotColor,
                          }}
                        />
                      )
                    )}
                    <div
                      className={cn(
                        "flex flex-1 justify-between leading-none",
                        nestLabel ? "items-end" : "items-center"
                      )}
                    >
                      {nestLabel && (
                        <div className={cn("font-medium", labelClassName)}>
                          {tooltipLabel}
                        </div>
                      )}
                      <div className="text-muted-foreground">
                        {cfg?.label || item.name}
                      </div>
                      {item.value != null && (
                        <div className="font-mono font-medium tabular-nums text-foreground">
                          {item.value.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  formatter(item.value, item.name, item, i, item.payload)
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }
)
ChartTooltipContent.displayName = "ChartTooltipContent"

/* -------------------------------------------------------------------------- */
/*  Legend                                                                    */
/* -------------------------------------------------------------------------- */
const ChartLegend = RechartsPrimitive.Legend

const ChartLegendContent = forwardRef(
  (
    {
      className,
      hideIcon = false,
      payload,
      verticalAlign = "bottom",
      nameKey,
    },
    ref
  ) => {
    const { config } = useChart()
    if (!payload?.length) return null

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-center gap-4",
          verticalAlign === "top" ? "pb-3" : "pt-3",
          className
        )}
      >
        {payload.map((item, i) => {
          const key = nameKey || item.dataKey || "value"
          const cfg = getPayloadConfigFromPayload(config, item, key)
          return (
            <div
              key={i}
              className="flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground"
            >
              {cfg?.icon && !hideIcon ? (
                <cfg.icon />
              ) : (
                <div
                  className="h-2 w-2 shrink-0 rounded-[2px]"
                  style={{ backgroundColor: item.color }}
                />
              )}
              {cfg?.label}
            </div>
          )
        })}
      </div>
    )
  }
)
ChartLegendContent.displayName = "ChartLegendContent"

/* -------------------------------------------------------------------------- */
/*  Utilidad                                                                  */
/* -------------------------------------------------------------------------- */
function getPayloadConfigFromPayload(config, payload, key) {
  if (!payload || typeof payload !== "object") return undefined
  const inner =
    payload.payload && typeof payload.payload === "object"
      ? payload.payload
      : payload
  return config[key] || config[inner[key]]
}

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
}
